/* =====================================================================
 %%% File Name  :
 %%% Authors      : Knut Sveidqvist, knut.sveidqvist@teliasonera.com
 %%%
 %%% Created      : 9/30/14 with IntelliJ IDEA.
 %%% Copyright    : TeliaSonera Sverige AB 2012
 %%%                Box 1218
 %%%                SE-751 42 Uppsala, Sweden
 %%%                The program may be used and/or copied only with the written
 %%%                permission from Telia Promotor AB, or in accordance with the
 %%%                terms and conditions stipulated in the agreement/contract
 %%%                under which the program has been supplied.
 %%%                All rights reserved.
 %%%
 %%% Purpose      : Implementation of functions controlling the execution of the
 %%%                COB user client.
 %%%
 %%% Changes      : 9/30/14 knsv01 First verison.
 %%%                2017-01-13 AnHj03 Moved ajax to a new module
 ========================================================================== */

import { createLogger } from '@telia-ace/edge-log';
import Q from 'q';

import { callWS } from './ajaxc';

const log = createLogger('utils/lang');

/**
 * Variable that will be set with the current language used by the client. In turn this
 * language setting is used by getStr when fetching a string fir a key for the GUI
 */
let lang;

export function setLang(langCode) {
  lang = langCode;
}

export function getLang() {
  return lang;
}

/**
 * Function that calculates what language to use. When the browser accept-lang header is
 * returned this function checks if a supported language is provided by the web
 * site. If not it takes the language from the browser and checks if that is
 * supported. If all fails the configured default language will be used. Once
 * the language has been decided the method fetched the localeStrings for that
 * language.
 *
 * The variable LANGUAGE is assumed to be set by the website using proactive web.
 *
 */
export const setupLanguage = (cgoConfig) => {
  const deferred = Q.defer();

  // Check if there is a language determined already
  const storedLang = sessionStorage.getItem('CallGuide.language');

  const saveAndExit = (language) => {
    log.debug(`Language negotiation completed. Language to be used is: ${language}`);
    // Save language for next time
    sessionStorage.setItem('CallGuide.language', language);
    lang = language;
    log.trace('Resolving with lang = ', language);
    deferred.resolve(language);
  };

  if (storedLang === null) {
    // Check if the site provides a language
    if (typeof window.LANGUAGE !== 'undefined') {
      // Check configuration if site language is available
      if (cgoConfig.languages[window.LANGUAGE] === '1') {
        log.debug(`Using language provide by site,(${window.LANGUAGE})`);
        lang = window.LANGUAGE;

        setTimeout(() => {
          saveAndExit(window.LANGUAGE);
        }, 1);

        return undefined;
      }

      log.debug(`Site language,(${window.LANGUAGE}), not among configured languages`);
    } else {
      log.debug('No language provided by site');
    }

    log.trace('Will fetch lang info');
    // No luck from the language provide by the site either, try checking the browser
    callWS(cgoConfig.langDetectUrl, {}, 15000).then(
      (languageHeader) => {
        log.trace('languageHeader', languageHeader);
        if (languageHeader?.AcceptLanguage) {
          const langLong = languageHeader.AcceptLanguage.toLowerCase();
          const navLang = langLong.substr(0, 2);

          // Note: If first language not is supported, go down in the list to
          // see if next language is supported.
          if (cgoConfig.languages[navLang] === '1') {
            log.debug(`Use language provided by navigator: ${navLang}`);
            saveAndExit(navLang);
            return undefined;
          }
          log.debug(`Language provided by browser (${navLang}) is not among configured languages.`);

          // For loop below is short and thus not dangerous to performance, disable eslint rule!
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of Object.entries(cgoConfig.languages)) {
            if (value === '1') {
              log.debug(`Use language provided by configuration: ${key}`);
              saveAndExit(key);
              return undefined;
            }
          }
        }
        // When everything else fails - use the default language
        log.debug(`Use default language: ${cgoConfig.defaultLanguage}`);
        saveAndExit(cgoConfig.defaultLanguage);
        return undefined;
      },
      (error) => {
        log.debug('No language detected, error: ', error);
        log.debug(`Use default language: ${cgoConfig.defaultLanguage}`);
        saveAndExit(cgoConfig.defaultLanguage);
        return undefined;
      }
    );
  } else {
    setTimeout(() => {
      saveAndExit(storedLang);
    }, 0);
  }

  return deferred.promise;
};
